import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
    useMemo,
} from "react";
import { ToastContainer, toast, ToastOptions } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ReceiveGroupCallInviteResponse } from "../interfaces/videoHubResponse/videoHubResponse";
import IncomingCallNotification from "../components/components/notifications/IncomingCallNotification";

// Custom error icon SVG
const ErrorIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_488_3710)">
            <path d="M10.0013 18.3334C14.6037 18.3334 18.3346 14.6024 18.3346 10C18.3346 5.39765 14.6037 1.66669 10.0013 1.66669C5.39893 1.66669 1.66797 5.39765 1.66797 10C1.66797 14.6024 5.39893 18.3334 10.0013 18.3334Z" 
                  stroke="#E7000B" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M10 6.66666V9.99999" 
                  stroke="#E7000B" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M10 13.3333H10.0083" 
                  stroke="#E7000B" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
        </g>
        <defs>
            <clipPath id="clip0_488_3710">
                <rect width="20" height="20" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

// Custom success icon SVG
const SuccessIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_488_3683)">
            <path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z" 
                  stroke="#00A63E" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M7.5 10L9.16667 11.6667L12.5 8.33334" 
                  stroke="#00A63E" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
        </g>
        <defs>
            <clipPath id="clip0_488_3683">
                <rect width="20" height="20" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

// Custom info icon SVG
const InfoIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_488_3696)">
            <path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z" 
                  stroke="#155DFC" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M10 13.3333V10" 
                  stroke="#155DFC" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M10 6.66666H10.0083" 
                  stroke="#155DFC" 
                  strokeWidth="1.66667" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
        </g>
        <defs>
            <clipPath id="clip0_488_3696">
                <rect width="20" height="20" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

// Override toast.error globally to use custom icon and 5-second duration
const originalToastError = toast.error;
toast.error = (message: any, options?: ToastOptions) => {
    return originalToastError(message, {
        ...options,
        icon: <ErrorIcon />,
        autoClose: options?.autoClose || 5000  // 5 seconds
    });
};

// Override toast.success globally to use custom icon and 5-second duration
const originalToastSuccess = toast.success;
toast.success = (message: any, options?: ToastOptions) => {
    return originalToastSuccess(message, {
        ...options,
        icon: <SuccessIcon />,
        autoClose: options?.autoClose || 5000  // 5 seconds
    });
};

// Override toast.info globally to use custom icon and 5-second duration
const originalToastInfo = toast.info;
toast.info = (message: any, options?: ToastOptions) => {
    return originalToastInfo(message, {
        ...options,
        icon: <InfoIcon />,
        autoClose: options?.autoClose || 5000  // 5 seconds
    });
};

interface NotificationContextProps {
    notify: (message: ReactNode, options?: ToastOptions) => void;
    notifyForCall: (hubResponse: ReceiveGroupCallInviteResponse,
        onAccept: () => void,
        onReject: () => void) => void;
    hideCallBanner: () => void;
}

const NotificationContext = createContext<NotificationContextProps>({
    notify: () => { },
    notifyForCall: () => { },
    hideCallBanner: () => { },
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [callData, setCallData] = useState<{
        hubResponse: ReceiveGroupCallInviteResponse;
        onAccept: () => void;
        onReject: () => void;
    } | null>(null);

    const callSound = useMemo(() => {
        const audio = new Audio("/sounds/callingbell.mp3");
        audio.loop = true;
        return audio;
    }, []);

    // Store timeout id
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Show toast notifications
    const notify = (message: ReactNode, options?: ToastOptions) => {
        console.log("Log: Notification triggered from NotificationProvider.tsx:notify", { message, options });
        
        // Default icon based on type if not provided
        const defaultIcon = options?.type === 'error' ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_488_3710)">
                                                <path d="M10.0013 18.3334C14.6037 18.3334 18.3346 14.6024 18.3346 10C18.3346 5.39765 14.6037 1.66669 10.0013 1.66669C5.39893 1.66669 1.66797 5.39765 1.66797 10C1.66797 14.6024 5.39893 18.3334 10.0013 18.3334Z" 
                                                      stroke="#E7000B" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 6.66666V9.99999" 
                                                      stroke="#E7000B" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 13.3333H10.0083" 
                                                      stroke="#E7000B" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_488_3710">
                                                    <rect width="20" height="20" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
        ) : options?.type === 'success' ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_488_3683)">
                                                <path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z" 
                                                      stroke="#00A63E" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M7.5 10L9.16667 11.6667L12.5 8.33334" 
                                                      stroke="#00A63E" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_488_3683">
                                                    <rect width="20" height="20" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
        ) : options?.type === 'info' ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_488_3696)">
                                                <path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z" 
                                                      stroke="#155DFC" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 13.3333V10" 
                                                      stroke="#155DFC" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 6.66666H10.0083" 
                                                      stroke="#155DFC" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_488_3696">
                                                    <rect width="20" height="20" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
        ) : options?.type === 'warning' ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="8" stroke="#FF9F1C" strokeWidth="2"/>
                <path d="M10 6V10" stroke="#FF9F1C" strokeWidth="2" strokeLinecap="round"/>
                <path d="M10 14H10.01" stroke="#FF9F1C" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        ) : undefined;

        toast(message, {
            ...options,
            icon: options?.icon || defaultIcon,
            autoClose: options?.autoClose || 5000,  // 5-second duration for notify function
        });
    };

    // Show a call notification with custom UI
    const notifyForCall = (
        hubResponse: ReceiveGroupCallInviteResponse,
        onAccept: () => void,
        onReject: () => void,
    ) => {
        console.log("Log: Notification triggered from NotificationProvider.tsx:notifyForCall", { hubResponse });
        setCallData({ hubResponse, onAccept, onReject });

        // ▶️ Start the sound
        callSound.play().catch((err) => {
            console.warn("Autoplay failed:", err);
        });

        // Clear any previous timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        // Set new timeout
        timeoutRef.current = setTimeout(() => {
            handleReject(); // Auto reject
        }, 60000);
    };

    useEffect(() => {
        if (!callData) {
            callSound.pause();
            callSound.currentTime = 0;
        }
    }, [callData, callSound]);

    const handleAccept = () => {
        callSound.pause();
        callSound.currentTime = 0;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        callData?.onAccept();
        setCallData(null);
    };

    const handleReject = () => {
        callSound.pause();
        callSound.currentTime = 0;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        callData?.onReject();
        setCallData(null);
    };

    console.log('CALL DATA', callData)

    // Hide the call banner (set callData to null)
    const hideCallBanner = () => {
        setCallData(null);
        callSound.pause();
        callSound.currentTime = 0;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    return (
        <NotificationContext.Provider value={{ notify, notifyForCall, hideCallBanner }}>
            {children}
            <ToastContainer
                stacked
                position="top-right"
                autoClose={5000}
                newestOnTop={true}
                closeOnClick
                pauseOnHover
                draggable
            />

            {/* Sample UI elements */}
            {/* <IncomingCallNotification
                callerId={"705HyjNk4BglfoIHBrgFHwq0yuG3"}
                callerName="Steve"
                isMeeting={false}
                onAccept={handleAccept}
                onReject={handleReject}
            /> */}

            {/* @TODO: Make sure that we have access to the userId of the user calling, 
            not just the display name */}

            {callData && (
                <IncomingCallNotification
                    callerId={callData.hubResponse.callerId || ""}
                    callerName={callData.hubResponse.callerDisplayName}
                    isMeeting={!!callData.hubResponse.calenderEventId}
                    onAccept={handleAccept}
                    onReject={handleReject}
                />
            )}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
